import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const PERIODS = ['today', 'yesterday', 'week', 'month'];
const PERIOD_LABELS = { today: 'Idag', yesterday: 'Igår', week: 'Vecka', month: 'Månad' };
const FORECAST_TABS = ['dayAhead', 'weekAhead', 'monthAhead'];
const FORECAST_LABELS = { dayAhead: 'Dag framåt', weekAhead: 'Vecka framåt', monthAhead: 'Månad framåt' };

const ZONE_COLORS = {
    SE1: '#59a14f',
    SE2: '#4e79a7',
    SE3: '#edc949',
    SE4: '#e15759',
};

export default function LoadDashboard() {
    const [activeTab, setActiveTab] = useState('actual'); // 'actual' | 'forecast'
    const [period, setPeriod] = useState('today');
    const [forecastTab, setForecastTab] = useState('dayAhead');
    const [actualData, setActualData] = useState(null);
    const [forecastData, setForecastData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchActual = async (p) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`/api/load?period=${p}`);
            setActualData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Kunde inte hämta faktisk belastningsdata');
        } finally {
            setLoading(false);
        }
    };

    const fetchForecast = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get('/api/load-forecast?regions=SE1,SE2,SE3,SE4');
            setForecastData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Kunde inte hämta prognosdata');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'actual') fetchActual(period);
        else fetchForecast();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, period]);

    const buildActualChartData = () => {
        if (!actualData?.regions) return null;
        const seZones = ['SE1', 'SE2', 'SE3', 'SE4'];
        const maxPoints = Math.max(...seZones.map(z => actualData.regions[z]?.hourly?.length || 0));
        if (maxPoints === 0) return null;

        const labels = Array.from({ length: maxPoints }, (_, i) => `H${i + 1}`);
        const datasets = seZones
            .filter(z => actualData.regions[z]?.hourly?.length > 0)
            .map(z => ({
                label: z,
                data: actualData.regions[z].hourly.map(h => h.load),
                borderColor: ZONE_COLORS[z],
                backgroundColor: ZONE_COLORS[z] + '20',
                fill: false,
                tension: 0.3,
                pointRadius: 2,
            }));

        return { labels, datasets };
    };

    const buildForecastChartData = () => {
        if (!forecastData?.regions) return null;
        const seZones = ['SE1', 'SE2', 'SE3', 'SE4'];
        const datasets = [];

        seZones.forEach(z => {
            const points = forecastData.regions[z]?.[forecastTab]?.points || [];
            if (points.length === 0) return;
            datasets.push({
                label: z,
                data: points.map(p => p.quantity),
                borderColor: ZONE_COLORS[z],
                backgroundColor: ZONE_COLORS[z] + '20',
                fill: false,
                tension: 0.3,
                pointRadius: 2,
            });
        });

        if (datasets.length === 0) return null;
        const maxLen = Math.max(...datasets.map(d => d.data.length));
        const labels = Array.from({ length: maxLen }, (_, i) => `P${i + 1}`);

        return { labels, datasets };
    };

    const actualChart = activeTab === 'actual' ? buildActualChartData() : null;
    const forecastChart = activeTab === 'forecast' ? buildForecastChartData() : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { labels: { color: '#9ca3af', boxWidth: 12 } },
            tooltip: { mode: 'index' }
        },
        scales: {
            x: { ticks: { color: '#6b7280' }, grid: { color: 'rgba(255,255,255,0.05)' } },
            y: {
                ticks: { color: '#6b7280', callback: v => `${v.toLocaleString()} MW` },
                grid: { color: 'rgba(255,255,255,0.05)' }
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto">
                <h1 className="text-3xl font-bold mb-2">Elförbrukning</h1>
                <p className="text-gray-400 mb-6">Faktisk &amp; prognostiserad totalbelastning per budzon · ENTSO-E A65</p>

                {/* Main Tabs */}
                <div className="flex gap-2 mb-6 border-b border-white/10 pb-0">
                    {['actual', 'forecast'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-5 py-2.5 font-medium capitalize rounded-t-lg transition-colors ${
                                activeTab === tab
                                    ? 'bg-white/10 text-white border-b-2 border-blue-500'
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            {tab === 'actual' ? 'Faktisk förbrukning' : 'Prognos'}
                        </button>
                    ))}
                </div>

                {/* Period / Forecast Type Selectors */}
                {activeTab === 'actual' && (
                    <div className="flex gap-2 mb-6">
                        {PERIODS.map(p => (
                            <button
                                key={p}
                                onClick={() => setPeriod(p)}
                                className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${
                                    period === p ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                }`}
                            >
                                {PERIOD_LABELS[p] || p}
                            </button>
                        ))}
                    </div>
                )}

                {activeTab === 'forecast' && (
                    <div className="flex gap-2 mb-6">
                        {FORECAST_TABS.map(t => (
                            <button
                                key={t}
                                onClick={() => setForecastTab(t)}
                                className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${
                                    forecastTab === t ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                }`}
                            >
                                {FORECAST_LABELS[t]}
                            </button>
                        ))}
                    </div>
                )}

                {loading && (
                    <div className="text-center py-20 text-gray-400">
                        <div className="animate-spin h-10 w-10 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                        Hämtar data...
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 text-red-300">{error}</div>
                )}

                {!loading && !error && (
                    <>
                        {/* Stats Cards - Actual */}
                        {activeTab === 'actual' && actualData?.countryTotals && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                                {Object.entries(actualData.countryTotals).map(([country, stats]) => (
                                    <div key={country} className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <h3 className="text-sm text-gray-400 mb-3">{country}</h3>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <p className="text-xl font-bold">{stats.average?.toLocaleString()}</p>
                                                <p className="text-xs text-gray-500">medel MW</p>
                                            </div>
                                            <div>
                                                <p className="text-xl font-bold">{stats.peak?.toLocaleString()}</p>
                                                <p className="text-xs text-gray-500">topp MW</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Stats Cards - Forecast */}
                        {activeTab === 'forecast' && forecastData?.regions && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                                {['SE1', 'SE2', 'SE3', 'SE4'].map(z => {
                                    const stats = forecastData.regions[z]?.[forecastTab]?.statistics;
                                    return (
                                        <div key={z} className="bg-white/5 border border-white/10 rounded-xl p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ZONE_COLORS[z] }} />
                                                <span className="font-semibold">{z}</span>
                                            </div>
                                            {stats ? (
                                                <>
                                                    <p className="text-xl font-bold">{stats.average?.toLocaleString()}</p>
                                                    <p className="text-xs text-gray-500">medel MW</p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {stats.min?.toLocaleString()} – {stats.max?.toLocaleString()} MW
                                                    </p>
                                                </>
                                            ) : (
                                                <p className="text-xs text-gray-600">Ingen data</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Chart */}
                        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                            <h2 className="text-lg font-semibold mb-4">
                                {activeTab === 'actual'
                                    ? `Faktisk förbrukning – ${PERIOD_LABELS[period] || period}`
                                    : `Prognos – ${FORECAST_LABELS[forecastTab]}`}
                            </h2>
                            <div className="h-72">
                                {(actualChart || forecastChart) ? (
                                    <Line data={actualChart || forecastChart} options={chartOptions} />
                                ) : (
                                    <div className="h-full flex items-center justify-center text-gray-600">
                                        Ingen diagramdata tillgänglig
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
